import { CarrierCode } from '../../../../models/carrier';
import { toDateStamp } from '../../../../models/dateStamp';
import { Id } from '../../../../models/id';
import {
  MetadataWithDisplayOrder,
  MetadataWithDisplayOrderAndDataProcessingOutputCode,
  MetadataWithDisplayOrderAndDataProcessingOutputCodeAndIsArchived,
  MetaDataWithDisplayOrderAndIsArchived,
} from '../../../../models/metadata';
import { ScidMailmarkBarcodeType } from '../../../scids/scid';
import { WorksheetConsumableType } from '../../consumables/worksheetConsumable';

export const sortedCellReferenceMaxLength = 200;

export type SortedCellConsumable = 'Magnums' | 'Pallets' | 'Cages' | 'Yorks' | 'NotRequired';
export type SortedCellConsumableMetadata = MetadataWithDisplayOrder<SortedCellConsumable> & {
  worksheetConsumableType: WorksheetConsumableType | null;
};

export const sortedCellFormatValues = ['Letter', 'LargeLetter', 'Packet'] as const;
export type SortedCellFormat = typeof sortedCellFormatValues[number];
export type SortedCellFormatMetadata = MetadataWithDisplayOrderAndDataProcessingOutputCode<
  SortedCellFormat
>;

export const sortedCellPresentationValues = ['Bag', 'Bundle', 'Tray', 'Pallets'] as const;
export type SortedCellPresentation = typeof sortedCellPresentationValues[number];
export type SortedCellPresentationMetadata = MetadataWithDisplayOrderAndDataProcessingOutputCode<
  SortedCellPresentation
> & {
  worksheetConsumableType: WorksheetConsumableType | null;
  defaultMaxWeightKg: number;
  maximumMaxWeightKg: number;
  validBundleLabelFormats: Array<SortedCellBundleLabelFormat> | null;
  defaultBundleLabelFormat: SortedCellBundleLabelFormat | null;
};

export const sortedCellServiceTypeValues = [
  'SeventyMailmark',
  'SeventyMailmarkEconomy',
  'SeventyOcr',
  'SeventyCatalogue',
  'FourteenHundredDirect',
  'SeventyResidue',
  'CatalogueMail',
  'Cms',
  'NineNineNine',
  'SeventyMailmarkCatalogue',
  'SeventyMailmarkCatalogueEconomy',
] as const;

export type SortedCellServiceType = typeof sortedCellServiceTypeValues[number];

export type SortedCellServiceTypeMetadata = MetadataWithDisplayOrderAndDataProcessingOutputCode<
  SortedCellServiceType
> & {
  validMailmarkBarcodes: Array<SortedCellMailmarkBarcode> | null;
  defaultMailmarkBarcode: SortedCellMailmarkBarcode | null;
  supportsScids: boolean;
};

export type SortedCellServiceTypeMetadataAndIsArchived = MetadataWithDisplayOrderAndDataProcessingOutputCodeAndIsArchived<
  SortedCellServiceType
> & {
  validMailmarkBarcodes: Array<SortedCellMailmarkBarcode> | null;
  defaultMailmarkBarcode: SortedCellMailmarkBarcode | null;
  supportsScids: boolean;
  isArchived: boolean;
};

// TODO: Set SortedCellServiceArchiveDateStamp to 2024-04-01 when this goes live
export const SortedCellServiceArchiveDateStamp = toDateStamp(new Date('2024-04-01'));

export const sortedCellVariantValues = [
  'GeneralMail',
  'BusinessMail',
  'PartiallyAddressed',
  'MagSubs',
  'AdMail',
  'AdMailRespE',
  'AdMailRespI',
] as const;

export type SortedCellVariant = typeof sortedCellVariantValues[number];

export type SortedCellVariantMetadata = MetadataWithDisplayOrderAndDataProcessingOutputCodeAndIsArchived<
  SortedCellVariant
>;
export const SortedCellVariantArchiveDateStamp = toDateStamp(new Date('2024-01-18'));

export type SortedCellBundleLabelFormat =
  | 'PdfOneUp'
  | 'PdfTwoUp'
  | 'PdfThreeUp'
  | 'PdfFourUp'
  | 'StickyLabels';
export type SortedCellBundleLabelFormatMetadata = MetadataWithDisplayOrder<
  SortedCellBundleLabelFormat
>;

export type SortedCellMailmarkBarcode =
  | 'SevenEncoded'
  | 'Seven'
  | 'NineEncoded'
  | 'Nine'
  | 'TwentyNineEncoded'
  | 'TwentyNine'
  | 'FourState';
export type SortedCellMailmarkBarcodeMetadata = MetadataWithDisplayOrder<
  SortedCellMailmarkBarcode
> & {
  scidMailmarkBarcodeType: ScidMailmarkBarcodeType;
};

export type SortedCell = {
  sortedCellId: Id;
  displayOrder: number;
  cellReference: string;
  serviceType: SortedCellServiceType;
  format: SortedCellFormat;
  variant: SortedCellVariant;
  quantity: number;
  weightInGrams: number;
  presentation: SortedCellPresentation;
  consumable: SortedCellConsumable;
  maxPresentationWeightKg: number;
  maxPresentationFill: number | null;
  bundleLabelFormat: SortedCellBundleLabelFormat | null;
  mailmarkBarcode: SortedCellMailmarkBarcode | null;
};

export type CarrierServiceFormatValidationConfiguration = {
  serviceTypesByCarrierCode: { [carrierCode in CarrierCode]: CarrierServiceTypes };
};

type CarrierServiceTypes = {
  carrier: CarrierCode;
  serviceTypesByCode: { [serviceType in SortedCellServiceType]?: ServiceTypeFormats };
};

type ServiceTypeFormats = {
  serviceTypeCode: SortedCellServiceType;
  isAllowedOnAgencyWorksheets: boolean;
  rulesByFormatCode: { [format in SortedCellFormat]?: CarrierServiceFormatRules };
};

export type CarrierServiceFormatRules = {
  allowedVariantCodesForNonAgencyAgreement: Array<SortedCellVariant>;
  allowedVariantCodesForAgencyAgreement: Array<SortedCellVariant>;
  allowedPresentationCodes: Array<SortedCellPresentation>;
  minWeight: number;
  maxWeight: number;
  selectionWarningsByPresentationCode: { [presentation in SortedCellPresentation]?: string };
};

export type PresentationConsumablesValidationConfiguration = {
  presentationConsumablesConfigurationByCarrierCode: {
    [carrierCode in CarrierCode]: CarrierPresentationConsumablesConfiguration;
  };
};

export type CarrierPresentationConsumablesConfiguration = {
  carrier: CarrierCode;
  allowedConsumablesByPresentationCode: {
    [presentation in SortedCellPresentation]?: PresentationAllowedConsumables;
  };
  defaultsAreOverriddenByMailingHouseCagesAndYorksPreferences: boolean;
  nonDefaultSelectionWarning: string | null;
};

export type PresentationAllowedConsumables = {
  presentationCode: SortedCellPresentation;
  defaultConsumableCode: SortedCellConsumable;
  allowedConsumableCodes: Array<SortedCellConsumable>;
};
